<template>
  <section class="Page" :style="{ height: pageHeight }">
    <Header
      class="Page__header"
      v-bind="accountHeader"
      :showServiceMenu="false"
    />
    <main ref="mainRef" class="Page__main">
      <GlobalNavigation
        class="Page__globalNavigation"
        :globalMenuItems="globalMenuItems"
      />
      <ScrollBox class="Page__scrollBox" always>
        <div ref="licenseDivRef" class="Page__licenseDiv">
          <section class="Page__licenseSection">
            <Breadcrumb class="Page__breadcrumb" :breadcrumb="breadcrumb" />
            <header class="Page__mainHeader">
              <Heading
                type="quaternary"
                level="1"
                :heading="license.licenseName"
                class="Page__heading"
              />
            </header>
            <!-- ライセンス利用状況 -->
            <div class="Page__licenseUsageSituationDiv">
              <LicenseSkeleton v-if="isAdminDataLoading" />
              <LicenseUsageSituation
                v-else
                ref="usage"
                class="Page__licenseUsageSituation"
                :licenseDisplayName="license.displayName"
                :licenseAssignmentCurrent="license.assignmentCurrent"
                :licenseAssignmentLimit="license.assignmentLimit"
              />
            </div>

            <LicenseManagement
              :licenseAssignmentCurrent="license.assignmentCurrent"
              :licenseAssignmentLimit="license.assignmentLimit"
              :licenseId="license.id"
              @onUpdateLicense="onUpdateLicense"
            ></LicenseManagement>
          </section>
        </div>
      </ScrollBox>
    </main>
  </section>
</template>
<script lang="ts">
export default {
  name: 'AdminContractLicense',
};
</script>

<script lang="ts" setup>
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import Breadcrumb from '@spectee/components/Molecules/Breadcrumb/index.vue';
import GlobalNavigation from '@spectee/components/Organisms/GlobalNavigation/index.vue';
import { globalMenuItems } from '../../../data/SideMenuItems';
import Header from '@spectee/components/Organisms/Header/index.vue';
import LicenseUsageSituation from '@admin/components/Organisms/LicenseUsageSituation/index.vue';
import { useRoute } from 'vue-router';
import LicenseManagement from '@spectee/components/Organisms/LicenseManagement/index.vue';
import LicenseSkeleton from './skeleton.vue';
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useGetAdminData } from '@spectee/graphql/queries/getAdminData';
import {
  getLicenseNameByServiceIdAndLicenseKey,
  getServiceNameById,
} from '@spectee/ts/utils';
import { serviceLicenseSettingData } from '@spectee/resources/serviceLicenseSetting';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import { useWindowHeight } from '@spectee/composables/useWindowHeight';
import messages from '../../../messages.json';
const { pageHeight } = useWindowHeight();

const accountHeader = ref({
  color: 'blue',
});

const unicodeSpace = '\u00A0\u00A0';
const route = useRoute();

const licenseId =
  typeof route.params.licenseId === 'string'
    ? route.params.licenseId
    : route.params.licenseId[0];
const planMasterId =
  typeof route.params.planMasterId === 'string'
    ? route.params.planMasterId
    : route.params.planMasterId[0];

const breadcrumb = computed(() => {
  const services = serviceLicenseSettingData.find(
    services => services.serviceId === planMasterId
  );
  const serviceName = planMasterId ? getServiceNameById(planMasterId) : '';

  const licenseName =
    services && licenseId
      ? getLicenseNameByServiceIdAndLicenseKey(services.serviceId, licenseId)
      : '';

  return [
    { link: '/', text: messages.heading.home },
    {
      text: `${serviceName} ${unicodeSpace} ${messages.heading.license} ${unicodeSpace}  ${licenseName}`,
    },
  ];
});

const mainRef = ref<HTMLElement | null>(null);
const licenseDivRef = ref<HTMLElement | null>(null);

const setContentsHeight = async () => {
  if (mainRef.value && licenseDivRef.value) {
    licenseDivRef.value.style.height = '';

    await nextTick();

    const mainStyle = getComputedStyle(mainRef.value);
    const mainHeight = parseFloat(mainStyle.height);
    const headerHeight = 24;
    const licenseContentHeight = mainHeight - headerHeight;

    const licenseDivStyle = getComputedStyle(licenseDivRef.value);
    const licenseDivHeight = parseFloat(licenseDivStyle.height);
    const licenseTableVisibleHeight = 800;
    const height =
      licenseContentHeight > licenseTableVisibleHeight
        ? licenseContentHeight
        : licenseDivHeight > licenseTableVisibleHeight
        ? licenseTableVisibleHeight
        : licenseDivHeight;
    licenseDivRef.value.style.height = `${height}px`;
  }
};

onMounted(() => {
  setContentsHeight();
  window.addEventListener('resize', setContentsHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', setContentsHeight);
});

const {
  useGetAdminDataRes: getAdminDataRes,
  loading: isAdminDataLoading,
  refetch: adminDataRefetch,
} = useGetAdminData();

const onUpdateLicense = async () => {
  await adminDataRefetch();
};
const license = computed(() => {
  const allServices = getAdminDataRes.value?.servicesData || [];

  for (const service of allServices) {
    const matchedLicense = service?.licenses.find(
      licenseItem => licenseItem.key === licenseId
    );
    if (matchedLicense) {
      return {
        id: matchedLicense.id,
        licenseName:
          getLicenseNameByServiceIdAndLicenseKey(
            service?.serviceId || '',
            matchedLicense.key
          ) || '',
        assignmentCurrent: matchedLicense.usedQuantity ?? 0,
        assignmentLimit: matchedLicense.quantity ?? 0,
      };
    }
  }
  return {
    id: '',
    accountId: '',
    displayName: '',
    assignmentCurrent: 0,
    assignmentLimit: 0,
  };
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

.Page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
  min-height: 0;
  min-width: $service-min-width;

  &__mainHeader {
    display: flex;
    align-items: center;
  }

  &__licenseUsageSituationDiv {
    display: flex;
    flex-direction: column;
  }

  &__licenseDiv {
    flex: 1;
    overflow: auto;
  }

  &__licenseSection {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  & &__header {
    position: unset;
  }

  & &__main {
    position: relative;
    display: flex;
    height: 100%;
    overflow: hidden;
    padding: 0;
    flex-grow: 1;
  }

  &__scrollBox {
    flex: 1;
  }

  &__breadcrumb,
  &__heading,
  &__licenseUsageSituationDiv {
    padding: $service-unit * 4 $service-unit * 8 0;
  }

  @media (max-width: 425px) {
    &__breadcrumb {
      font-size: $service-font-size-pp;
    }
  }

  @media (min-width: 768px) {
    & &__globalNavigation {
      position: unset;
    }
  }

  @media (max-width: 767px) {
    &__scrollBox {
      flex: 1;
      margin-left: $service-unit * 6;
      box-shadow: $service-shadow-mp;
    }
  }
}
</style>
